import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { PlusIcon, TypeIcon, MapPinIcon, PhoneIcon, TagIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
const TEMPLATES = [{
  id: 1,
  name: 'Standard Dealer Template',
  fields: 4,
  lastUsed: '2024-01-15'
}, {
  id: 2,
  name: 'Festival Offer Template',
  fields: 6,
  lastUsed: '2024-01-10'
}, {
  id: 3,
  name: 'Premium Launch Template',
  fields: 5,
  lastUsed: '2024-01-05'
}];
const FIELD_TYPES = [{
  id: 'dealer_name',
  label: 'Dealer Name',
  icon: <TypeIcon size={16} data-id="element-2664" />,
  auto: true
}, {
  id: 'phone',
  label: 'Phone Number',
  icon: <PhoneIcon size={16} data-id="element-2665" />,
  auto: true
}, {
  id: 'address',
  label: 'Address',
  icon: <MapPinIcon size={16} data-id="element-2666" />,
  auto: true
}, {
  id: 'pricing',
  label: 'State Pricing',
  icon: <TagIcon size={16} data-id="element-2667" />,
  auto: false
}, {
  id: 'offer',
  label: 'Offer Text',
  icon: <TagIcon size={16} data-id="element-2668" />,
  auto: false
}, {
  id: 'festival',
  label: 'Festival Text',
  icon: <TypeIcon size={16} data-id="element-2669" />,
  auto: false
}];
export function StylingTemplate() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [templateName, setTemplateName] = useState('');
  const toggleField = (fieldId: string) => {
    setSelectedFields(prev => prev.includes(fieldId) ? prev.filter(id => id !== fieldId) : [...prev, fieldId]);
  };
  const canContinue = mode === 'existing' && selectedTemplate !== null || mode === 'new' && templateName && selectedFields.length > 0;
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-2670">
      <Sidebar data-id="element-2671" />
      <main className="flex-1" data-id="element-2672">
        <Header data-id="element-2673" />
        <StepIndicator currentStep={4} steps={STEPS} data-id="element-2674" />
        <div className="px-6 py-8" data-id="element-2675">
          <div className="max-w-5xl mx-auto" data-id="element-2676">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-2677">
              Step 3: Styling Template
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-2678">
              Create or apply dynamic field templates for dealer customization
            </p>

            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6" data-id="element-2679">
              <div className="flex space-x-4 mb-6" data-id="element-2680">
                <button onClick={() => setMode('existing')} className={`px-6 py-2 rounded-md font-medium transition-colors ${mode === 'existing' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2681">
                  Use Existing Template
                </button>
                <button onClick={() => setMode('new')} className={`px-6 py-2 rounded-md font-medium transition-colors ${mode === 'new' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2682">
                  Create New Template
                </button>
              </div>

              {mode === 'existing' ? <div className="space-y-3" data-id="element-2683">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-2684">
                    Saved Templates
                  </h3>
                  {TEMPLATES.map(template => <button key={template.id} onClick={() => setSelectedTemplate(template.id)} className={`w-full p-4 rounded-lg border-2 text-left transition-all ${selectedTemplate === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2685">
                      <div className="flex items-center justify-between" data-id="element-2686">
                        <div data-id="element-2687">
                          <p className="font-medium text-gray-800" data-id="element-2688">
                            {template.name}
                          </p>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-2689">
                            {template.fields} dynamic fields • Last used:{' '}
                            {template.lastUsed}
                          </p>
                        </div>
                        {selectedTemplate === template.id && <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center" data-id="element-2690">
                            <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2691">
                              <path d="M5 13l4 4L19 7" data-id="element-2692"></path>
                            </svg>
                          </div>}
                      </div>
                    </button>)}
                </div> : <div className="space-y-6" data-id="element-2693">
                  <div data-id="element-2694">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2695">
                      Template Name
                    </label>
                    <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Enter template name" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2696" />
                  </div>

                  <div data-id="element-2697">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-2698">
                      Add Dynamic Fields
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-2699">
                      {FIELD_TYPES.map(field => <button key={field.id} onClick={() => toggleField(field.id)} className={`p-4 rounded-lg border-2 text-left transition-all ${selectedFields.includes(field.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2700">
                          <div className="flex items-center justify-between mb-2" data-id="element-2701">
                            <div className="flex items-center" data-id="element-2702">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mr-3" data-id="element-2703">
                                {field.icon}
                              </div>
                              <span className="font-medium text-gray-800" data-id="element-2704">
                                {field.label}
                              </span>
                            </div>
                            {selectedFields.includes(field.id) && <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center" data-id="element-2705">
                                <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2706">
                                  <path d="M5 13l4 4L19 7" data-id="element-2707"></path>
                                </svg>
                              </div>}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${field.auto ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-2708">
                            {field.auto ? 'Auto-filled' : 'Manual input'}
                          </span>
                        </button>)}
                    </div>
                  </div>

                  {selectedFields.length > 0 && <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2709">
                      <h4 className="font-medium text-gray-800 mb-3" data-id="element-2710">
                        Field Configuration
                      </h4>
                      <div className="space-y-3" data-id="element-2711">
                        {selectedFields.map(fieldId => {
                    const field = FIELD_TYPES.find(f => f.id === fieldId);
                    return <div key={fieldId} className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-2712">
                              <p className="font-medium text-sm text-gray-800 mb-2" data-id="element-2713">
                                {field?.label}
                              </p>
                              <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-2714">
                                <div data-id="element-2715">
                                  <label className="text-gray-600" data-id="element-2716">
                                    Font Size
                                  </label>
                                  <input type="number" defaultValue="16" className="w-full px-2 py-1 border border-gray-300 rounded mt-1" data-id="element-2717" />
                                </div>
                                <div data-id="element-2718">
                                  <label className="text-gray-600" data-id="element-2719">Color</label>
                                  <input type="color" defaultValue="#000000" className="w-full h-7 border border-gray-300 rounded mt-1" data-id="element-2720" />
                                </div>
                                <div data-id="element-2721">
                                  <label className="text-gray-600" data-id="element-2722">
                                    Alignment
                                  </label>
                                  <select className="w-full px-2 py-1 border border-gray-300 rounded mt-1" data-id="element-2723">
                                    <option data-id="element-2724">Left</option>
                                    <option data-id="element-2725">Center</option>
                                    <option data-id="element-2726">Right</option>
                                  </select>
                                </div>
                              </div>
                            </div>;
                  })}
                      </div>
                    </div>}
                </div>}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-2727">
              <h4 className="font-medium text-blue-900 mb-2" data-id="element-2728">
                Dynamic Field Types
              </h4>
              <div className="text-sm text-blue-800 space-y-1" data-id="element-2729">
                <p data-id="element-2730">
                  • <strong data-id="element-2731">Auto-filled:</strong> Dealer Name, Phone, Address,
                  Logo (from database)
                </p>
                <p data-id="element-2732">
                  • <strong data-id="element-2733">Manual:</strong> State Pricing, Offers, Festival
                  Text (configured in next step)
                </p>
              </div>
            </div>

            <div className="flex justify-between" data-id="element-2734">
              <button onClick={() => navigate('/dealerization/step-2')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2735">
                Back
              </button>
              <button onClick={() => navigate('/dealerization/step-4')} disabled={!canContinue} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-2736">
                Continue to Variable Content
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}